import { Box, Divider, Stack } from '@mui/material'
import type { DateTime } from 'luxon'
import type { KeyboardEvent } from 'react'
import { useCallback, useRef } from 'react'

import type { CascadeMarketId } from '@/features/bidding/types/market'

import CurrentTimeDisplay from './CurrentTimeDisplay'
import MarketSegment from './MarketSegment'
import { useMarketTimelineState } from './useMarketTimelineState'

interface MarketTimelineSelectorProps {
  selectedMarket: CascadeMarketId
  onSelectMarket: (id: CascadeMarketId) => void
  selectedDate: DateTime
  bidStatus?: Record<CascadeMarketId, boolean>
  clearingPrices?: Partial<Record<CascadeMarketId, string>>
  /** Markets that should be shown but not selectable */
  disabledMarkets?: CascadeMarketId[]
  /** Override current time for testing purposes */
  currentTimeOverride?: DateTime
}

function MarketTimelineSelector({
  selectedMarket,
  onSelectMarket,
  selectedDate,
  bidStatus = {
    mfrr: false,
    'da-wholesale': false,
    'fcr-d': false,
    'fcr-n': false,
  },
  clearingPrices = {},
  disabledMarkets = [],
  currentTimeOverride,
}: Readonly<MarketTimelineSelectorProps>) {
  const { currentTime, markets } = useMarketTimelineState({
    selectedDate,
    bidStatus,
    clearingPrices,
    currentTimeOverride,
  })

  const segmentRefs = useRef<(HTMLDivElement | null)[]>([])

  // Handle arrow key navigation between segments (skipping disabled markets)
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const enabledMarkets = markets.filter((m) => !disabledMarkets.includes(m.id))
      if (enabledMarkets.length === 0) return

      const currentEnabledIndex = enabledMarkets.findIndex((m) => m.id === selectedMarket)
      let newEnabledIndex = currentEnabledIndex

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        newEnabledIndex = currentEnabledIndex > 0 ? currentEnabledIndex - 1 : enabledMarkets.length - 1
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        newEnabledIndex = currentEnabledIndex < enabledMarkets.length - 1 ? currentEnabledIndex + 1 : 0
      }

      if (newEnabledIndex !== currentEnabledIndex) {
        const newMarket = enabledMarkets[newEnabledIndex]
        const originalIndex = markets.findIndex((m) => m.id === newMarket.id)
        onSelectMarket(newMarket.id)
        segmentRefs.current[originalIndex]?.focus()
      }
    },
    [markets, selectedMarket, onSelectMarket, disabledMarkets],
  )

  return (
    <Stack aria-label="Market timeline selector" direction="row" role="toolbar" onKeyDown={handleKeyDown}>
      {markets.map((market, index) => (
        <Box key={market.id} sx={{ display: 'flex' }}>
          <Divider flexItem orientation="vertical" sx={{ borderColor: 'rgba(255, 255, 255, 0.15)' }} />
          <Box
            ref={(el: HTMLDivElement | null) => {
              segmentRefs.current[index] = el
            }}
          >
            <MarketSegment
              isDisabled={disabledMarkets.includes(market.id)}
              isSelected={selectedMarket === market.id}
              market={market}
              onSelect={onSelectMarket}
            />
          </Box>
        </Box>
      ))}
      <Divider flexItem orientation="vertical" sx={{ borderColor: 'rgba(255, 255, 255, 0.15)' }} />
      <CurrentTimeDisplay currentTime={currentTime} />
    </Stack>
  )
}

export default MarketTimelineSelector
